import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { tripService, Trip, getTripImageSrc, type TripImage } from '../../api/trips';
import type { HistoryTrip } from '../../api/history.trip';
import { historyTripService } from '../../api/history.trip';
import {
  History,
  Search,
  RefreshCw,
  Car,
  MapPin,
  User,
  Eye,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
  Clock,
  CalendarDays,
  ImageIcon,
  Camera,
  Gauge,
  Timer,
  FileText,
  ListChecks,
} from 'lucide-react';
import {
  PageHeader,
  Button,
  Badge,
  Input,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
} from '../../components/ui/SharedLayout';

const ITEMS_PER_PAGE = 20;

const HistoricoDeViagens = () => {
  const { user, setPageTitle } = useAuth();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [tripHistories, setTripHistories] = useState<HistoryTrip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);

  useEffect(() => {
    setPageTitle('Histórico de Viagens');
  }, [setPageTitle]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (!user?.employee_id) {
      setTrips([]);
      setTripHistories([]);
      setError('Seu usuário não está vinculado a um funcionário.');
      setLoading(false);
      return;
    }

    try {
      const [allTrips, allHistories] = await Promise.all([
        tripService.getAll(),
        historyTripService.getAll(),
      ]);
      const userTrips = allTrips.filter(
        (trip) => trip.status !== 'pendente' && trip.employee_id === user.employee_id,
      );
      setTrips(userTrips);
      setTripHistories(allHistories);
    } catch (_err) {
      setError('Erro ao buscar histórico de viagens.');
      setTrips([]);
      setTripHistories([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const getLatestHistory = useCallback(
    (tripId: number) =>
      tripHistories
        .filter((history) => history.trip_id === tripId)
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0],
    [tripHistories],
  );

  const getTripHistoriesSorted = useCallback(
    (tripId: number) =>
      tripHistories
        .filter((h) => h.trip_id === tripId)
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()),
    [tripHistories],
  );

  const filteredTrips = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return trips.filter((trip) => {
      const latest = getLatestHistory(trip.id);
      const vehicleInfo = (latest?.vehicle_info || '').toLowerCase();
      const employeeInfo = (latest?.employee_info || '').toLowerCase();
      return (
        !term ||
        String(trip.id).includes(term) ||
        (trip.destination || '').toLowerCase().includes(term) ||
        (trip.purpose || '').toLowerCase().includes(term) ||
        vehicleInfo.includes(term) ||
        employeeInfo.includes(term) ||
        (trip.status || '').toLowerCase().includes(term)
      );
    });
  }, [trips, searchTerm, getLatestHistory]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredTrips.length / ITEMS_PER_PAGE)),
    [filteredTrips.length],
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [filteredTrips.length, currentPage, totalPages]);

  const { tripsPaginados, pageRange } = useMemo(() => {
    const total = filteredTrips.length;
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      tripsPaginados: filteredTrips.slice(startIndex, endIndex),
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredTrips, currentPage, totalPages]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  const formatStatus = (status?: string) => {
    switch (status) {
      case 'aprovada':
        return 'Aprovada';
      case 'em_andamento':
        return 'Em andamento';
      case 'concluida':
        return 'Concluída';
      case 'cancelada':
        return 'Cancelada';
      case 'rejeitada':
        return 'Rejeitada';
      default:
        return status ?? '-';
    }
  };

  const renderStatusBadge = (status?: string) => {
    switch (status) {
      case 'aprovada':
        return (
          <Badge tone="emerald" className="gap-1.5">
            <CheckCircle2 className="h-3 w-3 stroke-[2]" />
            Aprovada
          </Badge>
        );
      case 'em_andamento':
        return (
          <Badge tone="blue" className="gap-1.5">
            <Clock className="h-3 w-3 stroke-[2]" />
            Em andamento
          </Badge>
        );
      case 'concluida':
        return (
          <Badge tone="slate" className="gap-1.5">
            <CheckCircle2 className="h-3 w-3 stroke-[2]" />
            Concluída
          </Badge>
        );
      case 'cancelada':
        return (
          <Badge tone="slate" className="gap-1.5">
            <X className="h-3 w-3 stroke-[2]" />
            Cancelada
          </Badge>
        );
      case 'rejeitada':
        return (
          <Badge tone="red" className="gap-1.5">
            <XCircle className="h-3 w-3 stroke-[2]" />
            Rejeitada
          </Badge>
        );
      default:
        return (
          <Badge tone="amber" className="gap-1.5">
            <Clock className="h-3 w-3 stroke-[2]" />
            {formatStatus(status)}
          </Badge>
        );
    }
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const formatMinutes = (minutes?: number) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (!hours) return `${remainingMinutes} min`;
    if (!remainingMinutes) return `${hours}h`;
    return `${hours}h ${remainingMinutes}min`;
  };

  const ThumbnailCheckout = ({ trip }: { trip: Trip }) => {
    const checkoutImg = trip.images?.find(
      (img) => img.type === 'TRIP_CHECKOUT',
    ) as TripImage | undefined;
    const checkoutSrc = getTripImageSrc(checkoutImg);
    if (!checkoutSrc) {
      return (
        <span className="inline-flex items-center rounded-md bg-slate-50 px-2 py-1 text-[11.5px] font-medium text-slate-400 ring-1 ring-inset ring-slate-200">
          <ImageIcon className="h-3 w-3 stroke-[1.9] mr-1" />
          Sem foto
        </span>
      );
    }
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setExpandedImage(checkoutSrc);
        }}
        className="group relative block h-10 w-10 shrink-0 overflow-hidden rounded-lg border-2 border-white shadow-sm transition-all hover:z-10 hover:scale-110 hover:shadow-md"
        title="Clique para ampliar a foto de checkout"
      >
        <img src={checkoutSrc} alt="Checkout" className="h-full w-full object-cover" />
      </button>
    );
  };

  const renderVehicleCell = (trip: Trip) => {
    const latest = getLatestHistory(trip.id);
    const lines = latest?.vehicle_info
      ? latest.vehicle_info.split('\n').filter(Boolean)
      : [];
    const mainLine = lines[0] || `Veículo #${trip.vehicle_id}`;
    const subLine = lines.slice(1).join(' • ');
    return (
      <div
        className="min-w-0 w-full max-w-[260px] overflow-hidden"
        title={lines.join('\n')}
      >
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5 shrink-0 flex h-9 w-9 min-w-[36px] min-h-[36px] items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
            <Car className="h-[18px] w-[18px] stroke-[1.9]" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="block max-w-[200px] truncate text-[13.5px] font-semibold leading-tight text-slate-900">
              {mainLine}
            </span>
            {subLine && (
              <span className="block max-w-[200px] truncate mt-0.5 text-[11.5px] font-medium leading-tight text-slate-500">
                {subLine}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEmployeeCell = (trip: Trip) => {
    const latest = getLatestHistory(trip.id);
    const lines = latest?.employee_info
      ? latest.employee_info.split('\n').filter(Boolean)
      : [];
    const mainLine = lines[0] || `Funcionário #${trip.employee_id}`;
    const subLine = lines.slice(1).join(' • ');
    return (
      <div
        className="min-w-0 w-full max-w-[260px] overflow-hidden"
        title={lines.join('\n')}
      >
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5 shrink-0 flex h-9 w-9 min-w-[36px] min-h-[36px] items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200">
            <User className="h-[18px] w-[18px] stroke-[1.9]" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="block max-w-[200px] truncate text-[13.5px] font-semibold leading-tight text-slate-900">
              {mainLine}
            </span>
            {subLine && (
              <span className="block max-w-[200px] truncate mt-0.5 text-[11.5px] font-medium leading-tight text-slate-500">
                {subLine}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Histórico de Viagens" hideTitleInBody />

        <div className="flex flex-col gap-5">
          <div className="mb-2 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[15px] font-semibold leading-tight text-sky-600">Gestão de frota — Encerramentos</p>
              <h1 className="mt-1 text-[22px] sm:text-[26px] font-bold leading-[1.15] tracking-tight text-slate-900">
                Histórico das suas viagens.
                <span className="ml-1.5 text-slate-500 font-semibold text-[18px] sm:text-[20px]">
                  Registros concluídos, aprovados, rejeitados ou cancelados.
                </span>
              </h1>
              <p className="mt-2 text-[14px] leading-relaxed text-slate-500 max-w-3xl">
                Acompanhe todas as suas viagens finalizadas, aprovadas ou rejeitadas. Clique em "Visualizar" para ver detalhes completos, histórico de ações do setor, foto do checkout e motivos de cancelamento quando disponíveis.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="secondary" size="lg" onClick={() => void fetchData()} disabled={loading}>
                <RefreshCw className={`h-[18px] w-[18px] stroke-[2.1] ${loading ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
            </div>
          </div>

          {error && (
            <div className="inline-flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
              <div className="flex flex-col gap-0.5">
                <span className="text-[13px] font-bold leading-tight">Não foi possível carregar os dados</span>
                <span className="text-[12.5px] font-medium leading-tight text-rose-700/90">{error}</span>
              </div>
            </div>
          )}

          {loading ? (
            <SectionCard>
              <CardBody>
                <div className="flex items-center justify-center gap-2.5 py-20">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  <span className="text-[13.5px] font-semibold text-slate-600">Carregando histórico de viagens...</span>
                </div>
              </CardBody>
            </SectionCard>
          ) : trips.length === 0 ? (
            <SectionCard>
              <CardBody>
                <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 text-slate-400 shadow-[inset_0_2px_4px_rgba(15,23,42,0.04)]">
                    <History className="h-8 w-8 stroke-[1.8]" />
                  </div>
                  <div>
                    <div className="text-[15px] font-bold text-slate-700">Nenhuma viagem finalizada ainda</div>
                    <p className="mt-1 max-w-sm text-[13px] font-medium leading-relaxed text-slate-500">
                      Suas viagens concluídas, aprovadas ou rejeitadas aparecerão aqui automaticamente assim que forem processadas pelo sistema.
                    </p>
                  </div>
                </div>
              </CardBody>
            </SectionCard>
          ) : (
            <SectionCard>
              <CardHeader>
                <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 text-white shadow-[0_6px_16px_-8px_rgba(51,65,85,0.65)]">
                      <History className="h-[20px] w-[20px] stroke-[1.9]" />
                    </div>
                    <div>
                      <h2 className="text-[15px] font-bold tracking-tight text-slate-900">Suas viagens processadas</h2>
                      <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-slate-500">
                        Clique em qualquer linha para visualizar detalhes completos
                      </p>
                    </div>
                  </div>
                  <div className="relative w-full sm:w-[360px] shrink-0">
                    <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                      placeholder="Pesquisar destino, propósito, veículo, funcionário, status..."
                      className="!pl-10 h-10"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <ModernTable className="table-fixed layout-fixed">
                  <Thead>
                    <Tr>
                      <Th className="w-[11%] min-w-[110px] max-w-[130px]">Início</Th>
                      <Th className="w-[11%] min-w-[110px] max-w-[130px]">Fim</Th>
                      <Th className="w-[15%] min-w-[170px] max-w-[240px]">Veículo</Th>
                      <Th className="w-[14%] min-w-[150px] max-w-[220px]">Destino</Th>
                      <Th className="w-[14%] min-w-[150px] max-w-[220px]">Funcionário</Th>
                      <Th className="w-[8%] min-w-[110px] max-w-[140px]">Checkout</Th>
                      <Th className="w-[10%] min-w-[120px] max-w-[160px]">Status</Th>
                      <Th className="w-[6%] min-w-[80px] max-w-[100px]">Ações</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {tripsPaginados.length === 0 ? (
                      <Tr>
                        <Td colSpan={8}>
                          <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                              <Search className="h-7 w-7 stroke-[1.8]" />
                            </div>
                            <div className="text-[14px] font-bold text-slate-700">Nenhuma viagem encontrada</div>
                            <p className="max-w-sm text-[12.5px] font-medium leading-relaxed text-slate-500">
                              Nenhum resultado para a busca atual. Tente outro termo.
                            </p>
                          </div>
                        </Td>
                      </Tr>
                    ) : (
                      tripsPaginados.map((trip) => (
                        <Tr
                          key={trip.id}
                          onClick={() => setSelectedTrip(trip)}
                          className="cursor-pointer"
                        >
                          <Td>
                            <div className="flex flex-col gap-0.5">
                              <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-900 tabular-nums">
                                <CalendarDays className="h-[15px] w-[15px] text-slate-400 stroke-[1.8]" />
                                {formatDateTime(trip.start_date)}
                              </span>
                            </div>
                          </Td>
                          <Td>
                            <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                              <Clock className="h-[15px] w-[15px] text-slate-400 stroke-[1.8]" />
                              {formatDateTime(trip.end_date)}
                            </span>
                          </Td>
                          <Td>{renderVehicleCell(trip)}</Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[220px] overflow-hidden">
                              <div className="flex items-start gap-2">
                                <MapPin className="mt-[3px] h-3.5 w-3.5 shrink-0 text-slate-400 stroke-[1.8]" />
                                <div className="min-w-0 flex-1">
                                  <span className="block truncate text-[13.5px] font-semibold leading-tight text-slate-900" title={trip.destination || '-'}>
                                    {trip.destination || '-'}
                                  </span>
                                  {trip.purpose ? (
                                    <span className="mt-0.5 block truncate text-[11.5px] font-medium leading-tight text-slate-500" title={trip.purpose}>
                                      {trip.purpose}
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>{renderEmployeeCell(trip)}</Td>
                          <Td>
                            <div className="flex items-center gap-2">
                              <ThumbnailCheckout trip={trip} />
                            </div>
                          </Td>
                          <Td>
                            {renderStatusBadge(trip.status)}
                          </Td>
                          <Td>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTrip(trip);
                              }}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                              title="Visualizar detalhes"
                            >
                              <Eye className="h-[17px] w-[17px] stroke-[1.9]" />
                            </button>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </ModernTable>

                {pageRange.total > 0 ? (
                  <div className="mt-4 flex flex-col gap-3 border-t border-slate-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-[12.5px] font-medium leading-tight text-slate-500">
                      Mostrando{' '}
                      <span className="font-bold tabular-nums text-slate-800">{pageRange.start}</span>
                      {'–'}{' '}
                      <span className="font-bold tabular-nums text-slate-800">{pageRange.end}</span>
                      {' de '}
                      <span className="font-bold tabular-nums text-slate-800">{pageRange.total}</span>
                      {' viagem(ns)'}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                        aria-label="Primeira página"
                      >
                        <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                        aria-label="Página anterior"
                      >
                        <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                      </button>
                      <div className="inline-flex items-center gap-1 mx-1">
                        {getPageNumbers.map((n, idx) =>
                          n === '...' ? (
                            <span key={`dots-${idx}`} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]">…</span>
                          ) : (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setCurrentPage(n)}
                              className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1 ${
                                n === currentPage
                                  ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]'
                                  : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                              }`}
                            >
                              {n}
                            </button>
                          ),
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                        aria-label="Próxima página"
                      >
                        <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                        aria-label="Última página"
                      >
                        <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                      </button>
                    </div>
                  </div>
                ) : null}
              </CardBody>
            </SectionCard>
          )}
        </div>

        {expandedImage && (
          <Modal
            title="Foto do Check-Out"
            onClose={() => setExpandedImage(null)}
            maxWidthClass="max-w-3xl"
            footer={<Button variant="secondary" onClick={() => setExpandedImage(null)} className="h-10 px-5">Fechar</Button>}
          >
            <div className="flex items-center justify-center py-2 bg-slate-50 rounded-xl p-3">
              <img
                src={expandedImage}
                alt="Check-Out completo"
                className="w-full max-h-[72vh] h-auto rounded-lg object-contain shadow-md bg-white border border-slate-200"
              />
            </div>
          </Modal>
        )}

        {selectedTrip && (
          <Modal
            title={
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 text-white shadow-[0_6px_16px_-8px_rgba(51,65,85,0.65)]">
                  <Eye className="h-5 w-5 stroke-[1.9]" />
                </div>
                <div>
                  <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                    Detalhes da Viagem #{selectedTrip.id}
                  </div>
                  <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                    Informações completas e histórico de ações
                  </div>
                </div>
                <div className="ml-auto">
                  {renderStatusBadge(selectedTrip.status)}
                </div>
              </div>
            }
            onClose={() => setSelectedTrip(null)}
            maxWidthClass="max-w-4xl"
            footer={<Button variant="secondary" onClick={() => setSelectedTrip(null)} className="h-10 px-5">Voltar</Button>}
          >
            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Veículo</div>
                  <div className="mt-1 text-sm text-slate-900 whitespace-pre-line">
                    {(getLatestHistory(selectedTrip.id)?.vehicle_info || `Veículo #${selectedTrip.vehicle_id}`)
                      .split('\n')
                      .filter(Boolean)
                      .map((line, i) => (
                        <div key={i} className="font-semibold first:mt-0 mt-0.5">{line}</div>
                      ))}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Funcionário</div>
                  <div className="mt-1 text-sm text-slate-900 whitespace-pre-line">
                    {(getLatestHistory(selectedTrip.id)?.employee_info || `Funcionário #${selectedTrip.employee_id}`)
                      .split('\n')
                      .filter(Boolean)
                      .map((line, i) => (
                        <div key={i} className="font-semibold first:mt-0 mt-0.5">{line}</div>
                      ))}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Data de Início</div>
                  <div className="mt-1 flex items-center gap-2 text-sm text-slate-900 font-semibold tabular-nums">
                    <CalendarDays className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                    {formatDateTime(selectedTrip.start_date)}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Data de Fim</div>
                  <div className="mt-1 flex items-center gap-2 text-sm text-slate-900 font-semibold tabular-nums">
                    <Clock className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                    {formatDateTime(selectedTrip.end_date)}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Destino</div>
                  <div className="mt-1 flex items-start gap-2 text-sm text-slate-900 font-semibold">
                    <MapPin className="mt-[2px] h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                    <span className="truncate" title={selectedTrip.destination || '-'}>{selectedTrip.destination || '-'}</span>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Tempo de uso</div>
                  <div className="mt-1 flex items-center gap-2 text-sm text-slate-900 font-semibold tabular-nums">
                    <Timer className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                    {formatMinutes(selectedTrip.usage_duration_minutes)}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 md:col-span-2">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Propósito / Motivo</div>
                  <div className="mt-1 flex items-start gap-2 text-sm text-slate-900 font-semibold leading-relaxed whitespace-pre-wrap break-words">
                    <FileText className="mt-[2px] h-3.5 w-3.5 shrink-0 stroke-[1.8] text-slate-400" />
                    <span>{selectedTrip.purpose || 'Sem informação de propósito.'}</span>
                  </div>
                </div>
                {typeof selectedTrip.final_mileage === 'number' && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 md:col-span-2">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Quilometragem Final</div>
                    <div className="mt-1 flex items-center gap-2 text-sm text-slate-900 font-semibold tabular-nums">
                      <Gauge className="h-3.5 w-3.5 stroke-[1.8] text-indigo-600" />
                      {selectedTrip.final_mileage.toLocaleString('pt-BR')} km
                    </div>
                  </div>
                )}
                {selectedTrip.status === 'cancelada' && selectedTrip.cancellation_reason && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 md:col-span-2">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-rose-500">Motivo do Cancelamento</div>
                    <div className="mt-1 flex items-start gap-2 text-sm text-rose-800 font-semibold leading-relaxed whitespace-pre-wrap break-words">
                      <AlertCircle className="mt-[2px] h-3.5 w-3.5 shrink-0 stroke-[1.9] text-rose-600" />
                      <span>{selectedTrip.cancellation_reason}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
                <div className="mb-3 flex items-center gap-2">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-700 text-white">
                    <Camera className="h-[17px] w-[17px] stroke-[1.9]" />
                  </div>
                  <div>
                    <div className="text-[12.5px] font-bold uppercase tracking-[0.08em] text-slate-700">Foto do Check-Out</div>
                    <div className="text-[11.5px] font-medium text-slate-500 leading-tight">Registro de encerramento da viagem</div>
                  </div>
                </div>
                {(
                  () => {
                    const checkoutImgObj = selectedTrip.images?.find(
                      (img) => img.type === 'TRIP_CHECKOUT',
                    ) as TripImage | undefined;
                    const checkoutSrc = getTripImageSrc(checkoutImgObj);
                    if (!checkoutSrc) {
                      return (
                        <div className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-8 text-center">
                          <ImageIcon className="mx-auto h-8 w-8 stroke-[1.8] text-slate-400" />
                          <p className="mt-2 text-[12.5px] font-medium text-slate-500">Nenhuma foto de check-out registrada para esta viagem.</p>
                        </div>
                      );
                    }
                    return (
                      <button
                        type="button"
                        onClick={() => setExpandedImage(checkoutSrc)}
                        className="group block w-full text-left"
                      >
                        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white p-2 transition hover:border-blue-300 hover:shadow-sm">
                          <img
                            src={checkoutSrc}
                            alt="Check-Out"
                            className="h-auto max-h-[360px] w-full rounded-md object-contain bg-slate-100 transition group-hover:opacity-95"
                          />
                          <p className="mt-2 flex items-center justify-center gap-1.5 text-[11.5px] font-semibold text-slate-600 group-hover:text-blue-700">
                            <Eye className="h-3.5 w-3.5 stroke-[1.9]" />
                            Clique para ampliar
                          </p>
                        </div>
                      </button>
                    );
                  }
                )()}
              </div>

              <div className="rounded-xl border border-slate-200 bg-white px-4 py-4">
                <div className="mb-3 flex items-center gap-2">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 via-blue-500 to-sky-600 text-white">
                    <ListChecks className="h-[17px] w-[17px] stroke-[1.9]" />
                  </div>
                  <div>
                    <div className="text-[12.5px] font-bold uppercase tracking-[0.08em] text-slate-700">Histórico de Ações</div>
                    <div className="text-[11.5px] font-medium text-slate-500 leading-tight">Timeline de eventos da viagem</div>
                  </div>
                </div>
                {getTripHistoriesSorted(selectedTrip.id).length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center">
                    <ListChecks className="mx-auto h-7 w-7 stroke-[1.8] text-slate-400" />
                    <p className="mt-2 text-[12.5px] font-medium text-slate-500">Nenhuma ação registrada no histórico desta viagem.</p>
                  </div>
                ) : (
                  <ol className="relative border-l border-slate-200 ml-2 space-y-4">
                    {getTripHistoriesSorted(selectedTrip.id).map((h) => (
                      <li key={h.id} className="ml-5 relative">
                        <span className="absolute -left-[27px] top-0 flex h-4 w-4 items-center justify-center rounded-full border-[3px] border-white bg-gradient-to-br from-blue-500 via-indigo-500 to-indigo-600 shadow-sm" />
                        <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-3.5 py-2.5">
                          <div className="flex items-start justify-between gap-3">
                            <div className="text-[13px] font-semibold leading-tight text-slate-900">{h.action}</div>
                            <div className="text-[11.5px] font-medium tabular-nums text-slate-500 whitespace-nowrap">
                              {formatDateTime(h.created_at)}
                            </div>
                          </div>
                          {h.details && (
                            <div className="mt-1 text-[12px] font-medium leading-relaxed text-slate-600 whitespace-pre-wrap break-words">
                              {h.details}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default HistoricoDeViagens;
